import {attachFrontendTrackingIds} from '@udemy/browse-event-tracking';
import {DiscoveryUnit} from '@udemy/discovery-api';
import {
    type CursorMap,
    type ItemMapper,
    type UnitMapperOptions,
    fetchMoreRecommendationItems,
    isCourseRecommendation,
    pipe,
    RecommendationContextSection,
    RecommendationItem,
    toDiscoveryItem,
    toDiscoveryUnits,
    withCursors,
} from '@udemy/react-recommendations';

export type {CursorMap} from '@udemy/react-recommendations';
export {extractCursors} from '@udemy/react-recommendations';

export const isRecommendationItemIncluded = (item: RecommendationItem): boolean =>
    !isCourseRecommendation(item) || !!item.course;

const attachTracking = (unit: Partial<DiscoveryUnit>): Partial<DiscoveryUnit> => {
    if (unit.items) {
        attachFrontendTrackingIds(unit.items);
    }
    return unit;
};

export const toTrackedDiscoveryUnits = (
    sections: RecommendationContextSection[],
    cursors?: CursorMap,
    options: UnitMapperOptions = {},
): Partial<DiscoveryUnit>[] =>
    toDiscoveryUnits(sections, options).map(pipe(withCursors(cursors), attachTracking));

export const fetchMoreTrackedItems = async (
    cursor: string,
    requestedItemCount: number,
    itemMapper: ItemMapper = toDiscoveryItem,
    signal?: AbortSignal,
): Promise<{
    items: Partial<DiscoveryUnit>['items'];
    nextCursor: string | null;
}> => {
    const {moreRecommendationItems} = await fetchMoreRecommendationItems({
        cursor,
        requestedItemCount,
        signal,
    });

    const validItems = (moreRecommendationItems.items as RecommendationItem[])
        .filter(isRecommendationItemIncluded)
        .map(itemMapper);

    attachFrontendTrackingIds(validItems);

    return {
        items: validItems,
        nextCursor: moreRecommendationItems.itemsCursor ?? null,
    };
};
